import { useState, useEffect } from "react";
import { getAvailableDataSources, extractTableData, validateTableData, ExcelTableData } from "../../../shared/excelDataExtractor";
import { ApiError, default as SmartColumnApiService } from "../../../shared/smartColumnApiService";
import pivotTableApiService, { PivotTableGenerateRequest, PivotTableGenerateResponse, ValueObj } from "../../../shared/pivotTableApiService";
import { POLLING } from "../../../shared/constants";



export const usePivotTableColumn = () => {
    const [selectedSource, setSelectedSource] = useState<string>("");
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [showValidation, setShowValidation] = useState<boolean>(false);
    const [tableOptions, setTableOptions] = useState<string[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(true);
    const [userIntent, setUserIntent] = useState<string>("");
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [selectedTableData, setSelectedTableData] = useState<ExcelTableData | null>(null);
    const [allDataRows, setAllDataRows] = useState<any[][]>([]);
    const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
    const [error, setError] = useState<string>("");


    useEffect(() => {
        const loadDataSources = async () => {
            try {
                setIsLoadingOptions(true);
                const sources = await getAvailableDataSources();
                setTableOptions(sources);

                if (sources.length === 0) {
                    console.warn("No data sheets found.");
                }
            } catch (error) {
                console.error("Error loading data sources:", error);
            } finally {
                setIsLoadingOptions(false);
            }
        };

        loadDataSources();
    }, []);

    useEffect(() => {
        const POLL_INTERVAL_MS = POLLING.PIVOT_TABLE_INTERVAL_MS;

        const pollForNewSheets = async () => {
            try {
                const currentSources = await getAvailableDataSources();

                setTableOptions(prevOptions => {
                    const newSheets = currentSources.filter(sheet => !prevOptions.includes(sheet));

                    if (newSheets.length > 0) {
                        console.log(`🔄 New sheets detected: ${newSheets.join(', ')}`);
                        return currentSources;
                    }

                    const removedSheets = prevOptions.filter(sheet => !currentSources.includes(sheet));
                    if (removedSheets.length > 0) {
                        console.log(`🔄 Sheets removed: ${removedSheets.join(', ')}`);
                        return currentSources;
                    }

                    return prevOptions;
                });
            } catch (error) {
                console.warn("Error polling for new sheets:", error);
            }
        };

        const pollInterval = setInterval(pollForNewSheets, POLL_INTERVAL_MS);

        return () => {
            clearInterval(pollInterval);
        };
    }, []);

    const handleSourceSelect = async (source: string) => {
        setSelectedSource(source);
        setDropdownOpen(false);
        setShowValidation(false);
        setSelectedSuggestions(new Set());
        setAllDataRows([]);
        setError("");

        try {
            const tableData = await extractTableData(source);
            const validation = validateTableData(tableData);

            if (!validation.valid) {
                console.error("Invalid table data:", validation.message);
                setError("Invalid table data. Please select a different source.");
                return;
            }

            setSelectedTableData(tableData);
            setAllDataRows(tableData.data || []);
        } catch (error) {
            console.error("Error extracting table data:", error);
            setError("Failed to extract table data. Please try again.");
        }
    };


    const handleGenerate = async () => {
        if (!selectedSource) {
            setShowValidation(true);
            return;
        }

        if (!userIntent.trim()) {
            setError("Please enter an intent describing what kind of columns to generate.");
            return;
        }

        if (!selectedTableData) {
            setError("No table data available. Please select a source.");
            return;
        }

        if (!selectedTableData.headers || selectedTableData.headers.length === 0) {
            setError("Table has no headers. Please select a valid table.");
            return;
        }

        const dataRows = selectedTableData.data || [];
        if (dataRows.length === 0) {
            setError("Table has no data rows. Please select a table with data.");
            return;
        }

        try {
            setIsGenerating(true);
            setError("");
            console.log('user intent is', userIntent);
            console.log('selected table data is', selectedTableData);
            const sheetName = selectedTableData.sheetName;
            const range = selectedTableData.range;
            const headers = selectedTableData.headers || [];

            const tableWithHeaders = [headers, ...selectedTableData.data];

            const request: PivotTableGenerateRequest = {
                table: tableWithHeaders,
                user_intent: userIntent,
            };
            const result = await pivotTableApiService.generatePivotTable(request);
            console.log('pivot table result is', result);

            const rows = result.rows;
            const columns = result.columns;
            const values = result.values;

            await generatePivotTable(sheetName, range, rows, columns, values);

        } catch (error) {
            console.error("Error generating Pivot Table:", error);
            const errorMessage = SmartColumnApiService.formatErrorMessage(error as ApiError);
            setError(errorMessage);
        } finally {
            setIsGenerating(false);
        }
    };

    function capitalizeFirstLetter(str) {
        if (str === null || str === undefined || str.length === 0) {
            return ""; // Handles empty, null, or undefined strings
        }
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const generatePivotTable = async (sheetName: string, range: string, rows: string[], columns: string[], values: ValueObj[]) => {
        return await Excel.run(async (context) => {
            const EXCEL_AGGREGATION = {
                'average': Excel.AggregationFunction.average,
                "sum": Excel.AggregationFunction.sum,
                "count": Excel.AggregationFunction.count,
                "min": Excel.AggregationFunction.min,
                "max": Excel.AggregationFunction.max
            }
            
            const sheet = context.workbook.worksheets.getItem(sheetName);
            const sourceRange = sheet.getRange(range);

            const existingSheets = context.workbook.worksheets;
            existingSheets.load("items/name");
            await context.sync();
            const existingSheetNames = existingSheets.items.map(sheet => sheet.name);

            let pivotSheetName = sheetName + " Pivot";
            let counter = 2;
            while (existingSheetNames.includes(pivotSheetName)) {
                pivotSheetName = `${sheetName} Pivot ${counter}`;
                counter++;
            }

            const pivotSheet = context.workbook.worksheets.add(pivotSheetName);

            const pivotTable = context.workbook.pivotTables.add("PivotTable1", sourceRange, pivotSheet.getRange("A3"));

            rows.forEach(row => {
                pivotTable.rowHierarchies.add(pivotTable.hierarchies.getItem(row));
            });

            columns.forEach(column => {
                pivotTable.columnHierarchies.add(pivotTable.hierarchies.getItem(column));
            });

            values.forEach((value: ValueObj) => {
                pivotTable.dataHierarchies.add(pivotTable.hierarchies.getItem(value.field));
            });

            pivotTable.dataHierarchies.load("items/name");
            await context.sync();

            const dataHierarchies = pivotTable.dataHierarchies.items;

            values.forEach((value) => {
                const dataHierarchy = dataHierarchies.find((item) => item.name.endsWith(value.field));
                if (dataHierarchy) {
                    dataHierarchy.summarizeBy = EXCEL_AGGREGATION[value.aggregation];
                    dataHierarchy.name = `${capitalizeFirstLetter(value.aggregation)} of ${value.field}`;
                }
            })

            await context.sync();

            pivotSheet.activate();
            await context.sync();
        });
    }



    return {
        selectedSuggestions,
        isGenerating,
        selectedTableData,
        allDataRows,
        selectedSource,
        dropdownOpen,
        setDropdownOpen,
        showValidation,
        tableOptions,
        isLoadingOptions,
        userIntent,
        setUserIntent,
        handleSourceSelect,
        error,
        handleGenerate
    }

}